import React, { useMemo } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Badge from '@/components/Typography/Badge';
import ToolFlagBadges from '@/components/Tools/ToolFlagBadges';
import TaskTemplateFavoriteHeart from '@/components/Tasks/TaskTemplateFavoriteHeart';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useModalStore, useAuthStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import { paletteForSlug } from '@/utils/agent_catalog';

const SCHEDULE_LABELS = {
  once: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  annually: 'Yearly',
};

const TIME_OF_DAY_LABELS = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

const createStyles = (colors, isXS, isSM, isMD, isLG, isXL, isAltBGColor) => StyleSheet.create({
  templateCard: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    padding: isSM ? 12 : 14,
    borderRadius: 12,
    backgroundColor: isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    // Responsive widths: 1 col (mobile) -> 2 col (sm) -> 3 col (md+) -> 4 col (xl)
    width: (isXS || Platform.OS === 'macos') ? '100%' : isSM ? 'calc(50% - 6px)' : isXL ? 'calc(25% - 9px)' : 'calc(33.333% - 8px)',
    minHeight: isXS ? 110 : 130,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    overflow: 'hidden',
  },
  templateCardCompact: {
    minHeight: isXS ? 100 : 110,
    padding: isXS ? 10 : 12,
    gap: 8,
  },
  hoveredTemplateCard: {
    backgroundColor: isAltBGColor ? colors.solid_color_secondary_dark : colors.solid_color_tertiary,
    borderColor: colors.border_color_primary,
  },
  templateHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
  },
  iconContainer: {
    width: isSM ? 44 : 48,
    height: isSM ? 44 : 48,
    borderRadius: 12,
    backgroundColor: colors.solid_color_secondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 4,
  },
  templateContent: {
    flex: 1,
    width: '100%',
  },
  templateName: {
    marginBottom: 4,
  },
  templateDescription: {
    lineHeight: isSM ? 18 : 20,
    marginBottom: 8,
  },
  templateMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 'auto',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  ctaButton: {
    marginTop: 'auto',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: colors.accent_color_primary,
    alignSelf: 'flex-start',
  },
  ctaButtonText: {
    color: '#fff',
  },
});

// Category accents come from the shared `colorForSlug` hash so a task in
// a given category matches every other catalog surface for that category.

function TemplateCard({ template, onPress, variant = 'default', style: styleProp }) {
  const { colors, theme } = useTheme();
  const { isXS, isSM, isMD, isLG, isXL } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);
  const requireAuth = useRequireAuth();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(() => createStyles(colors, isXS, isSM, isMD, isLG, isXL, isAltBGColor), [colors, isXS, isSM, isMD, isLG, isXL, isAltBGColor]);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);

  const categoryPalette = useMemo(
    () => paletteForSlug(template?.category?.slug || template?.category || ''),
    [template],
  );
  const iconColor = categoryPalette.fg;
  const iconBgColor = categoryPalette.bg;

  const scheduleLabel = useMemo(() => {
    const type = template.defaultSchedule?.type || 'daily';
    return SCHEDULE_LABELS[type] || type;
  }, [template.defaultSchedule]);

  const timeLabel = useMemo(() => {
    const timeOfDay = template.defaultSchedule?.timeOfDay;
    if (!timeOfDay) return null;
    const hour = timeOfDay.hour;
    if (hour >= 5 && hour < 12) return TIME_OF_DAY_LABELS.morning;
    if (hour >= 12 && hour < 17) return TIME_OF_DAY_LABELS.afternoon;
    return TIME_OF_DAY_LABELS.evening;
  }, [template.defaultSchedule]);

  const handlePress = () => {
    if (onPress) {
      onPress(template);
      return;
    }
    requireAuth(
      () => openModal('SetupTaskFromTemplateModal', { template }),
      {
        source: 'task_template_card',
        title: `Sign in to set up ${template?.name || 'this task'}`,
        subtitle: 'Scheduled tasks run on your account so we know when (and for whom) to fire them.',
        icon: 'calendar-clock',
      },
    );
  };

  const cardStyle = useMemo(() => {
    const baseStyles = [styles.templateCard];
    if (variant === 'compact') {
      baseStyles.push(styles.templateCardCompact);
    }
    return baseStyles;
  }, [styles, variant]);

  return (
    <Pressable
      style={({ hovered }) => [
        ...cardStyle,
        hovered && styles.hoveredTemplateCard,
        styleProp,
      ]}
      onPress={handlePress}
    >
      <View style={styles.templateHeader}>
        <View style={[styles.iconContainer, { backgroundColor: iconBgColor }]}>
          <Icon 
            name={template.icon || 'clipboard-list'} 
            size={isSM ? 22 : 24} 
            color={iconColor} 
          />
        </View>
        <View style={styles.badgeContainer}>
          {template.flags?.isFeatured && (
            <ToolFlagBadges tool={{ flags: { isFeatured: true } }} />
          )}
          {template.flags?.isPremium && !isPlus && (
            <Badge
              icon="sparkles"
              text="PLUS"
              isPlus
            />
          )}
          <TaskTemplateFavoriteHeart template={template} />
        </View>
      </View>

      <View style={styles.templateContent}>
        <Text
          size={isXS ? 'n' : 'm'}
          color='primary'
          weight='bold'
          style={styles.templateName}
          numberOfLines={2}
        >
          {template.name}
        </Text>
        <Text 
          style={styles.templateDescription} 
          size='s' 
          color='secondary' 
          numberOfLines={2}
        >
          {template.shortDescription || template.description}
        </Text>
      </View>

      <View style={styles.templateMeta}>
        <View style={styles.metaItem}>
          <Icon name="calendar" size={12} color={colors.text_color_secondary} />
          <Text size='xs' color='secondary'>{scheduleLabel}</Text>
        </View>
        {timeLabel && (
          <View style={styles.metaItem}>
            <Icon name="clock" size={12} color={colors.text_color_secondary} />
            <Text size='xs' color='secondary'>{timeLabel}</Text>
          </View>
        )}
      </View>
    </Pressable>
  );
}

// Loading placeholder component
export function TemplateCardPlaceholder() {
  const { colors, theme } = useTheme();
  const { isXS, isSM, isMD, isLG, isXL } = useResponsiveness();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(() => createStyles(colors, isXS, isSM, isMD, isLG, isXL, isAltBGColor), [colors, isXS, isSM, isMD, isLG, isXL, isAltBGColor]);

  return (
    <View style={styles.templateCard}>
      <View style={styles.templateHeader}>
        <View style={[styles.iconContainer, { opacity: 0.5 }]} />
      </View>
      <View style={styles.templateContent}>
        <View style={{ 
          height: 16, 
          width: '70%', 
          backgroundColor: colors.solid_color_secondary, 
          borderRadius: 4,
          marginBottom: 8,
        }} />
        <View style={{ 
          height: 12, 
          width: '100%', 
          backgroundColor: colors.solid_color_secondary, 
          borderRadius: 4,
          marginBottom: 4,
        }} />
        <View style={{ 
          height: 12, 
          width: '60%', 
          backgroundColor: colors.solid_color_secondary, 
          borderRadius: 4,
        }} />
      </View>
    </View>
  );
}

export default TemplateCard;
